import { create } from "zustand";

const STORAGE_KEY = "rentmaadi-theme";

export type ThemePreference = "light" | "dark" | "system";

function getStored(): ThemePreference {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return "system";
}

function persist(theme: ThemePreference) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, theme);
}

export function getResolvedTheme(): "light" | "dark" {
  const pref = getStored();
  if (pref === "light") return "light";
  if (pref === "dark") return "dark";
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyToDocument() {
  if (typeof document === "undefined") return;
  const resolved = getResolvedTheme();
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

interface ThemeState {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
  /** Resolved effective theme (light or dark) for UI */
  resolved: "light" | "dark";
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: getStored(),
  resolved: getResolvedTheme(),

  setTheme(theme) {
    persist(theme);
    set({ theme });
    const resolved = getResolvedTheme();
    set({ resolved });
    applyToDocument();
  },
}));

// Apply on load and when system preference changes
if (typeof window !== "undefined") {
  applyToDocument();
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    const state = useThemeStore.getState();
    if (state.theme === "system") {
      const resolved = getResolvedTheme();
      useThemeStore.setState({ resolved });
      applyToDocument();
    }
  });
}
